import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useCampaignData } from "@/contexts/campaign-data";
import { useAccounts } from "@/contexts/account-context";
import { SHEET_TABS } from "@/lib/constants";
import { LoadingSpinner } from "@/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { AllProcessedHierarchicalMetrics } from "@/contexts/campaign-data";
import { DataNode } from "@/lib/hierarchical-data-utils";

interface BreadcrumbItem {
  id: string;
  name: string;
  level: "root" | "campaign" | "adgroup";
  entityId?: string; // ID of the campaign or adgroup for navigation
}

interface CurrentDisplayInfo {
  title: string;
  breadcrumbs: BreadcrumbItem[];
}

// Type for sorting configuration
interface SortConfig {
  key: keyof Pick<DataNode, "value" | "prevValue" | "change">;
  direction: "asc" | "desc";
}

// Helper function for recursive sorting
const sortNodesRecursively = (
  nodes: DataNode[],
  config: SortConfig,
): DataNode[] => {
  // Create a new array for the sorted nodes at the current level
  const sortedNodes = [...nodes].sort((a, b) => {
    const valA = a[config.key];
    const valB = b[config.key];

    if (valA < valB) {
      return config.direction === "asc" ? -1 : 1;
    }
    if (valA > valB) {
      return config.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Recursively sort children and map to new node objects
  return sortedNodes.map((node) => {
    if (node.children && node.children.length > 0) {
      return {
        ...node, // Spread existing node properties
        children: sortNodesRecursively(node.children, config), // Recursively sort children
      };
    }
    return node; // Return node as is (it's from a new array 'sortedNodes' or a new object if it had children)
  });
};

const GoogleAdsPerformanceMap = () => {
  const {
    data, // Raw data, may not be directly needed anymore if allProcessedMetrics is sufficient
    loadingStatus,
    allProcessedMetrics, // New from context
    isProcessingHierarchy, // New from context
  } = useCampaignData();
  const { activeAccount } = useAccounts();
  const accountCurrency = activeAccount?.currency || "$";

  const [selectedMetric, setSelectedMetric] = useState<string>("impr");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentDisplay, setCurrentDisplay] = useState<CurrentDisplayInfo>({
    title: "Google Ads Performance",
    breadcrumbs: [],
  });
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null); // State for sorting

  // Get the processed nodes for the currently selected metric
  const currentHierarchicalNodes: DataNode[] = useMemo(() => {
    if (!allProcessedMetrics || !selectedMetric) return [];
    return (
      allProcessedMetrics[
        selectedMetric as keyof AllProcessedHierarchicalMetrics
      ]?.nodes || []
    );
  }, [allProcessedMetrics, selectedMetric]);

  // Get the account total for the currently selected metric
  const currentAccountTotal: number = useMemo(() => {
    if (!allProcessedMetrics || !selectedMetric) return 0;
    return (
      allProcessedMetrics[
        selectedMetric as keyof AllProcessedHierarchicalMetrics
      ]?.accountTotal || 0
    );
  }, [allProcessedMetrics, selectedMetric]);

  // Sort data recursively if sortConfig is set
  const displayedData = useMemo(() => {
    if (sortConfig === null) {
      return currentHierarchicalNodes; // Use default sort from context
    }
    return sortNodesRecursively(currentHierarchicalNodes, sortConfig);
  }, [currentHierarchicalNodes, sortConfig]);

  const getColorForChange = useCallback((change: number): string => {
    if (change <= -15) return "text-red-700"; // Darker Red
    if (change <= -10) return "text-red-600";
    if (change <= -5) return "text-red-500";
    if (change < 0) return "text-red-400"; // Lighter Red
    if (change == 0) return "text-gray-500"; // Neutral grey

    if (change >= 15) return "text-green-700"; // Darker Green
    if (change >= 10) return "text-green-600";
    if (change >= 5) return "text-green-500";
    return "text-green-400"; // Lighter Green
  }, []);

  const handleToggleExpand = useCallback((rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const updateBreadcrumbs = useCallback(
    (node?: DataNode) => {
      const newBreadcrumbs: BreadcrumbItem[] = [];
      let title = "Google Ads Performance";

      if (node) {
        const pathParts = node.id.split("|");
        if (node.level === "campaign" || pathParts.length >= 1) {
          const campaignId = pathParts[0];
          const campaign = currentHierarchicalNodes.find(
            (c) => c.id === campaignId,
          );
          if (campaign) {
            newBreadcrumbs.push({
              id: campaign.id,
              name: campaign.name,
              level: "campaign",
              entityId: campaign.id,
            });
            title = `Campaign: ${campaign.name}`;
          }
        }
        if (node.level === "adgroup" || pathParts.length >= 2) {
          const campaignId = pathParts[0];
          const adGroupId = node.id;
          const campaign = currentHierarchicalNodes.find(
            (c) => c.id === campaignId,
          );
          const adGroup = campaign?.children?.find((ag) => ag.id === adGroupId);
          if (adGroup) {
            if (newBreadcrumbs.length === 0) {
              // If campaign wasn't added (e.g. direct link/future feature)
              const parentCampaign = currentHierarchicalNodes.find(
                (c) => c.id === campaignId,
              );
              if (parentCampaign)
                newBreadcrumbs.push({
                  id: parentCampaign.id,
                  name: parentCampaign.name,
                  level: "campaign",
                  entityId: parentCampaign.id,
                });
            }
            newBreadcrumbs.push({
              id: adGroup.id,
              name: adGroup.name,
              level: "adgroup",
              entityId: adGroup.id,
            });
            title = `Ad Group: ${adGroup.name}`;
          }
        }
      }
      setCurrentDisplay({ title, breadcrumbs: newBreadcrumbs });
    },
    [currentHierarchicalNodes],
  );

  const handleBreadcrumbClick = (crumb: BreadcrumbItem) => {
    if (crumb.level === "root") {
      setExpandedRows(new Set());
      setCurrentDisplay({ title: "Google Ads Performance", breadcrumbs: [] });
    } else if (crumb.level === "campaign" && crumb.entityId) {
      const campaignNode = currentHierarchicalNodes.find(
        (c) => c.id === crumb.entityId,
      );
      setExpandedRows((prev) => {
        const newSet = new Set<string>();
        if (crumb.entityId) newSet.add(crumb.entityId);
        return newSet;
      });
      if (campaignNode) updateBreadcrumbs(campaignNode);
    } else if (crumb.level === "adgroup" && crumb.entityId) {
      const pathParts = crumb.entityId.split("|");
      const campaignId = pathParts[0];
      const adGroupId = crumb.entityId;
      const campaignNode = currentHierarchicalNodes.find(
        (c) => c.id === campaignId,
      );
      const adGroupNode = campaignNode?.children?.find(
        (ag) => ag.id === adGroupId,
      );

      setExpandedRows((prev) => {
        const newSet = new Set<string>();
        newSet.add(campaignId);
        newSet.add(adGroupId);
        return newSet;
      });
      if (adGroupNode) updateBreadcrumbs(adGroupNode);
    }
  };

  const formatValue = useCallback(
    (value: number): string => {
      if (["cost", "value"].includes(selectedMetric)) {
        return `${accountCurrency}${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    [selectedMetric, accountCurrency],
  );

  // Function to handle sort requests
  const requestSort = (key: SortConfig["key"]) => {
    // Default to descending sort on first click for a new column
    let direction: "asc" | "desc" = "desc";

    if (sortConfig && sortConfig.key === key) {
      // Toggle direction if clicking the same column
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    } else {
      // If it's a new column, direction is already set to 'desc' (our new default)
    }
    setSortConfig({ key, direction });
  };

  const renderRow = (
    node: DataNode,
    indentLevel: number,
    totalAccountValueForPercent: number,
  ) => {
    const isExpanded = expandedRows.has(node.id);
    const canExpand =
      node.level !== "keyword" && node.children && node.children.length > 0;

    return (
      <React.Fragment key={node.id}>
        <TableRow
          onClick={() => {
            if (canExpand) handleToggleExpand(node.id);
            if (isExpanded && canExpand) {
              if (node.level === "campaign") updateBreadcrumbs();
              else if (node.level === "adgroup") {
                const campaignId = node.id.split("|")[0];
                const campaignNode = currentHierarchicalNodes.find(
                  (c) => c.id === campaignId,
                );
                if (campaignNode) updateBreadcrumbs(campaignNode);
              }
            } else if (!isExpanded && canExpand) {
              updateBreadcrumbs(node);
            } else if (node.level === "keyword") {
              const pathParts = node.id.split("|");
              if (pathParts.length > 1) {
                const adGroupId = `${pathParts[0]}|${pathParts[1]}`;
                const campaign = currentHierarchicalNodes.find(
                  (c) => c.id === pathParts[0],
                );
                const adGroup = campaign?.children?.find(
                  (ag) => ag.id === adGroupId,
                );
                if (adGroup) updateBreadcrumbs(adGroup);
              }
            } else if (
              !canExpand &&
              (node.level === "adgroup" || node.level === "campaign")
            ) {
              updateBreadcrumbs(node);
            }
          }}
          className={
            canExpand ? "cursor-pointer hover:bg-muted/50" : "hover:bg-muted/50"
          }
        >
          <TableCell style={{ paddingLeft: `${1 + indentLevel * 1.5}rem` }}>
            <div className="flex items-center">
              {canExpand &&
                (isExpanded ? (
                  <ChevronDown className="h-4 w-4 mr-1" />
                ) : (
                  <ChevronRight className="h-4 w-4 mr-1" />
                ))}
              {!canExpand && <span className="w-5 mr-1 inline-block"></span>}
              {node.name}
            </div>
          </TableCell>
          <TableCell className="text-left">
            {node.level === "keyword" && node.keywordMatchType && (
              <span className={getMatchTypeBadgeClasses(node.keywordMatchType)}>
                {node.keywordMatchType.toUpperCase()}
              </span>
            )}
          </TableCell>
          <TableCell className="text-right">
            {formatValue(node.value)}
          </TableCell>
          <TableCell className="text-right">
            {formatValue(node.prevValue)}
          </TableCell>
          <TableCell
            className={`text-right font-semibold ${getColorForChange(node.change)}`}
          >
            {node.change > 0 ? "+" : ""}
            {node.change}%
          </TableCell>
          <TableCell className="text-right">
            {totalAccountValueForPercent > 0
              ? `${((node.value / totalAccountValueForPercent) * 100).toFixed(1)}%`
              : "0.0%"}
          </TableCell>
        </TableRow>
        {isExpanded &&
          node.children &&
          node.children.map((childNode) =>
            renderRow(childNode, indentLevel + 1, totalAccountValueForPercent),
          )}
      </React.Fragment>
    );
  };

  // Combine loading states
  const isOverallLoading =
    loadingStatus === "initial" ||
    loadingStatus === "refresh" ||
    isProcessingHierarchy;

  if (isOverallLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }
  // Ensure allProcessedMetrics is not null and the specific metric has data
  if (
    !allProcessedMetrics ||
    (currentHierarchicalNodes.length === 0 &&
      loadingStatus === "idle" &&
      !isProcessingHierarchy)
  ) {
    const noDataTitle = activeAccount?.isSampleAccount
      ? "Sample Data Loaded"
      : "No Data Available";
    const noDataMessage =
      activeAccount?.isSampleAccount && currentHierarchicalNodes.length === 0
        ? "The sample data seems to be empty for the selected metric or doesn't contain keyword-level performance for hierarchy."
        : "No performance data found for the selected metric, or the data hasn't been processed into the hierarchical view yet. Ensure data is synced and processing is complete.";

    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
        <h2 className="text-xl font-bold mb-4">{noDataTitle}</h2>
        <p className="text-muted-foreground">{noDataMessage}</p>
        {activeAccount?.isSampleAccount &&
          currentHierarchicalNodes.length === 0 && (
            <p className="mt-2 text-sm">
              Selected metric: {selectedMetric}. Try another metric or check
              console for processing details.
            </p>
          )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen max-h-screen w-full bg-gray-50">
      <div className="flex items-center justify-between p-3 bg-white border-b">
        <h1 className="text-xl font-semibold">{currentDisplay.title}</h1>
        <div className="flex items-center space-x-3">
          <div>
            <Select
              value={selectedMetric}
              onValueChange={(value) => {
                setSelectedMetric(value);
                setExpandedRows(new Set());
                updateBreadcrumbs();
              }}
            >
              <SelectTrigger className="w-40 text-xs">
                <SelectValue placeholder="Metric" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="impr">Impressions</SelectItem>
                <SelectItem value="clicks">Clicks</SelectItem>
                <SelectItem value="cost">Cost</SelectItem>
                <SelectItem value="conv">Conversions</SelectItem>
                <SelectItem value="value">Conv. Value</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center px-3 py-2 bg-white border-b text-xs">
        {currentDisplay.breadcrumbs.length === 0 && (
          <button
            className="text-gray-700 hover:underline"
            onClick={() =>
              handleBreadcrumbClick({
                id: "root",
                name: "All Campaigns",
                level: "root",
              })
            }
          >
            All Campaigns
          </button>
        )}
        {currentDisplay.breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id + "-" + index}>
            {index === 0 && currentDisplay.breadcrumbs.length > 0 && (
              <button
                className="text-gray-700 hover:underline mr-1"
                onClick={() =>
                  handleBreadcrumbClick({
                    id: "root",
                    name: "All Campaigns",
                    level: "root",
                  })
                }
              >
                All Campaigns
              </button>
            )}
            {index > 0 && <span className="mx-1 text-gray-400">/</span>}
            <button
              className={`hover:underline ${index === currentDisplay.breadcrumbs.length - 1 ? "font-semibold text-blue-600" : "text-gray-700"}`}
              onClick={() => handleBreadcrumbClick(crumb)}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="flex-grow overflow-auto p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">Name</TableHead>
              <TableHead className="w-[10%]">Match Type</TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => requestSort("value")}
              >
                Current ({selectedMetric})
                {sortConfig?.key === "value" &&
                  (sortConfig.direction === "asc" ? (
                    <ArrowUp className="inline h-4 w-4 ml-1" />
                  ) : (
                    <ArrowDown className="inline h-4 w-4 ml-1" />
                  ))}
              </TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => requestSort("prevValue")}
              >
                Previous ({selectedMetric})
                {sortConfig?.key === "prevValue" &&
                  (sortConfig.direction === "asc" ? (
                    <ArrowUp className="inline h-4 w-4 ml-1" />
                  ) : (
                    <ArrowDown className="inline h-4 w-4 ml-1" />
                  ))}
              </TableHead>
              <TableHead
                className="text-right cursor-pointer"
                onClick={() => requestSort("change")}
              >
                Change
                {sortConfig?.key === "change" &&
                  (sortConfig.direction === "asc" ? (
                    <ArrowUp className="inline h-4 w-4 ml-1" />
                  ) : (
                    <ArrowDown className="inline h-4 w-4 ml-1" />
                  ))}
              </TableHead>
              <TableHead className="text-right">% of Account</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedData.length > 0 ? (
              displayedData.map((campaignNode) =>
                renderRow(campaignNode, 0, currentAccountTotal),
              )
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-muted-foreground py-8"
                >
                  {isOverallLoading
                    ? "Loading data..."
                    : "No data to display for the current selection."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="p-3 text-xs text-gray-600 border-t bg-white">
        <p className="font-semibold">Notes:</p>
        <ul className="list-disc list-inside ml-2">
          <li>Data represents Search campaigns only.</li>
          <li>"Current" refers to the last 30 days.</li>
          <li>
            "Previous" refers to the 30 days prior to the current period (i.e.,
            31-60 days ago).
          </li>
        </ul>
      </div>
    </div>
  );
};

// Placeholder function, actual implementation might be more complex or exist elsewhere
const getMatchTypeBadgeClasses = (matchType: string): string => {
  switch (matchType?.toLowerCase()) {
    case "exact":
      return "bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium";
    case "phrase":
      return "bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-medium";
    case "broad":
      return "bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs font-medium";
    default:
      return "bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium";
  }
};

export default GoogleAdsPerformanceMap;
